import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { TextReveal } from "./animations";
import DisciplineGlyph from "./hero/DisciplineGlyph";

type Layer = {
  label: string;
  caption: string;
  moves: string[];
};

const LAYERS: Layer[] = [
  {
    label: "Direction",
    caption: "Define the position and the problem worth solving.",
    moves: [
      "Vision, opportunity and product direction",
      "Business, brand and customer needs in one view",
    ],
  },
  {
    label: "Product",
    caption: "Turn the direction into a coherent product.",
    moves: [
      "Concepts, priorities and interaction models",
      "From early prototype through to shipped experience",
    ],
  },
  {
    label: "Systems",
    caption: "Make quality repeatable beyond a single release.",
    moves: [
      "Design, brand and content systems",
      "Governance that keeps teams aligned at scale",
    ],
  },
  {
    label: "AI",
    caption: "Build intelligence into the operating model.",
    moves: [
      "Knowledge, retrieval and agent workflows",
      "Evaluation, control and human review",
    ],
  },
  {
    label: "Impact",
    caption: "Leave the organization more capable than before.",
    moves: [
      "Adoption, quality and measurable outcomes",
      "Capability transferred to internal teams",
    ],
  },
];



const SWEEP_MS = 2200;
const SPRING = { type: "spring" as const, stiffness: 180, damping: 26, mass: 0.7 };

const HeroThesis = () => {
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  const [hovering, setHovering] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const cellRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [rail, setRail] = useState({ left: 0, width: 0 });

  // Track viewport so we only run the horizontal-accordion math on md+
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  // Measure active cell to position the top accent rail
  useLayoutEffect(() => {
    const measure = () => {
      const cell = cellRefs.current[active];
      const row = rowRef.current;
      if (!cell || !row) return;
      const c = cell.getBoundingClientRect();
      const r = row.getBoundingClientRect();
      setRail({ left: c.left - r.left, width: c.width });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (rowRef.current) ro.observe(rowRef.current);
    cellRefs.current.forEach((el) => el && ro.observe(el));
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [active]);

  // Cursor-tracking spotlight via CSS variables
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = rowRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    el.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      setActive((s) => (s + 1) % LAYERS.length);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive((s) => (s - 1 + LAYERS.length) % LAYERS.length);
    }
  };

  const activeLayer = LAYERS[active];

  return (
    <section className="relative border-b border-border bg-background">
      {/* ── Masthead ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-12 border-b border-border">
        <div className="md:col-span-9 px-6 md:px-8 py-10 md:py-14 border-b md:border-b-0 md:border-r border-border">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground block mb-6">
            ↳ How I work
          </span>

          <TextReveal delay={0.1}>
            <h2 className="font-display text-[clamp(2.25rem,5.2vw,4.75rem)] leading-[0.94] uppercase tracking-tight text-foreground max-w-[18ch]">
              I DEFINE THE DIRECTION. DESIGN THE PRODUCT.&nbsp;
              <span className="text-muted-foreground">
                {"\n"}BUILD THE SYSTEMS THAT MAKE IT SCALE.
              </span>
            </h2>
          </TextReveal>
        </div>

        <div className="md:col-span-3 px-6 md:px-8 py-8 md:py-10 flex flex-col justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground block mb-6">
            Practice
          </span>

          <div>
            <p className="font-display text-xl uppercase text-foreground mb-3">
              STRATEGY, MADE OPERATIONAL.
            </p>
            <p className="text-[13px] text-muted-foreground leading-relaxed">
              Embedded leadership across product, design and AI systems.
            </p>
          </div>
        </div>

      </div>

      {/* ── Operating stack, horizontal accordion (desktop) / vertical (mobile) ── */}
      <div
        ref={rowRef}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onMouseMove={handleMouseMove}
        onFocus={() => setHovering(true)}
        onBlur={() => setHovering(false)}
        onKeyDown={handleKey}
        tabIndex={0}
        role="tablist"
        aria-label="Operating layers"
        className="group/row relative outline-none focus-visible:bg-foreground/[0.015] overflow-hidden"
        style={
          {
            ["--mx" as never]: "-200px",
            ["--my" as never]: "-200px",
          } as React.CSSProperties
        }
      >
        {/* Cursor spotlight */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover/row:opacity-100 z-[5]"
          style={{
            background:
              "radial-gradient(320px circle at var(--mx) var(--my), hsl(var(--accent) / 0.07), transparent 60%)",
          }}
        />

        {/* Animated accent rail, slides + scales to active cell width */}
        <div className="absolute top-0 left-0 right-0 h-px bg-foreground/10 z-20 pointer-events-none">
          <motion.div
            className="absolute top-0 h-px bg-accent"
            animate={{ left: rail.left, width: rail.width }}
            initial={false}
            transition={reduceMotion ? { duration: 0 } : SPRING}
          />
          <motion.div
            className="absolute -top-[2px] h-[5px] w-[5px] rounded-full bg-accent"
            animate={{ left: rail.left + rail.width / 2 - 2.5 }}
            initial={false}
            transition={reduceMotion ? { duration: 0 } : SPRING}
            style={{
              boxShadow: "0 0 12px hsl(var(--accent) / 0.8)",
            }}
          />
        </div>

        {/* Stack: vertical on mobile, horizontal flex accordion on md+ */}
        <div className="relative flex flex-col md:flex-row md:h-[260px]">
          {LAYERS.map((layer, i) => {
            const isActive = active === i;
            const isLast = i === LAYERS.length - 1;

            return (
              <motion.button
                key={layer.label}
                ref={(el) => (cellRefs.current[i] = el)}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(i)}
                animate={isDesktop ? { flexGrow: isActive ? 5 : 1 } : { flexGrow: 0 }}
                initial={false}
                transition={reduceMotion ? { duration: 0 } : SPRING}
                className={[
                  "group relative text-left outline-none overflow-hidden",
                  "min-w-0 md:min-h-0 w-full md:w-auto",
                  !isLast ? "border-b md:border-b-0 md:border-r border-border" : "",
                  isActive
                    ? "bg-foreground/[0.025]"
                    : "bg-transparent hover:bg-foreground/[0.012]",
                  "transition-colors duration-300",
                ].join(" ")}
                style={isDesktop ? { flexBasis: 0 } : undefined}
              >
                {/* ───────── MOBILE LAYOUT (always-visible compact row) ───────── */}
                <div className="md:hidden">
                  <div className="flex items-center gap-4 px-5 py-3.5">
                    <span
                      className={`font-mono text-[10px] tracking-[0.2em] tabular-nums transition-colors duration-300 w-5 ${
                        isActive ? "text-accent" : "text-muted-foreground/50"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className={`font-display text-sm uppercase tracking-tight leading-none transition-colors duration-300 flex-1 ${
                        isActive ? "text-foreground" : "text-foreground/55"
                      }`}
                    >
                      {layer.label}
                    </span>
                    <div className="w-10 h-4 flex items-center justify-end shrink-0">
                      <DisciplineGlyph
                        index={i}
                        state={isActive ? "active" : "lit"}
                      />
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.div
                        key="m-expand"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={
                          reduceMotion
                            ? { duration: 0 }
                            : { duration: 0.32, ease: [0.25, 0.4, 0.25, 1] }
                        }
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5 pt-1 pl-[44px]">
                          <p className="text-[13px] leading-relaxed text-foreground/85 mb-3">
                            {layer.caption}
                          </p>
                          <ul className="space-y-1.5">
                            {layer.moves.map((m) => (
                              <li key={m} className="flex gap-2 font-mono text-[10px] leading-relaxed text-foreground/60">
                                <span className="text-accent/60">/</span>
                                <span>{m}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* ───────── DESKTOP LAYOUT (horizontal accordion) ───────── */}
                <div className="hidden md:flex absolute inset-0 flex-col">
                  {/* Collapsed state, pinned to left edge so it doesn't reflow as the cell grows */}
                  <motion.div
                    aria-hidden={isActive}
                    animate={{ opacity: isActive ? 0 : 1 }}
                    transition={{ duration: reduceMotion ? 0 : 0.2 }}
                    className="absolute inset-y-0 left-0 w-[72px] flex flex-col items-center justify-between py-6 px-2 pointer-events-none"
                  >
                    <span
                      className={`font-mono text-[10px] tracking-[0.2em] tabular-nums transition-colors duration-300 ${
                        isActive ? "text-accent" : "text-muted-foreground/50"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>

                    {/* Vertical label */}
                    <span
                      className="font-display text-sm uppercase tracking-[0.18em] leading-none text-foreground/55 whitespace-nowrap"
                      style={{
                        writingMode: "vertical-rl",
                        transform: "rotate(180deg)",
                      }}
                    >
                      {layer.label}
                    </span>

                    <div className="h-4 flex items-center">
                      <DisciplineGlyph index={i} state="lit" />
                    </div>
                  </motion.div>


                  {/* Expanded state, visible when active */}
                  <motion.div
                    aria-hidden={!isActive}
                    animate={{ opacity: isActive ? 1 : 0 }}
                    transition={{
                      duration: reduceMotion ? 0 : 0.28,
                      ease: [0.25, 0.4, 0.25, 1],
                    }}
                    className={`absolute inset-0 flex flex-col p-8 ${isActive ? "" : "pointer-events-none"}`}

                  >
                    <div className="flex items-baseline gap-3 mb-5">
                      <span className="font-mono text-[11px] tracking-[0.2em] text-accent tabular-nums">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-display text-2xl uppercase tracking-tight leading-none text-foreground">
                        {layer.label}
                      </span>
                    </div>

                    <div className="h-4 mb-4 flex items-center">
                      <DisciplineGlyph index={i} state="active" />
                    </div>

                    <p className="text-[15px] leading-snug text-foreground/85 max-w-md mb-4">
                      {layer.caption}
                    </p>

                    <ul className="space-y-1 max-w-md mb-auto">
                      {layer.moves.map((m) => (
                        <li key={m} className="flex gap-2 font-mono text-[11px] leading-relaxed text-foreground/55">
                          <span className="text-accent/60">/</span>
                          <span>{m}</span>
                        </li>
                      ))}
                    </ul>

                  </motion.div>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Footer rail */}
        <div className="relative flex items-center justify-between gap-4 px-5 md:px-8 py-3 border-t border-border bg-foreground/[0.015]">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            ↳ Explore operating layers
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground tabular-nums">
            <span className="text-accent">{String(active + 1).padStart(2, "0")}</span>
            <span className="text-muted-foreground/40"> / {String(LAYERS.length).padStart(2, "0")}</span>
            <span className="text-foreground/70 ml-3">{activeLayer.label}</span>
          </span>
        </div>
      </div>
    </section>
  );
};

export default HeroThesis;
